import type { ReactElement, MouseEvent, CSSProperties } from "react";

import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Size variant options
 */
type AuthorSize = "small" | "medium" | "large";

/**
 * ClickableAuthor component props
 */
interface ClickableAuthorProps {
  userId?: string;
  name?: string;
  picture?: string;
  size?: AuthorSize;
  showName?: boolean;
  showAvatar?: boolean;
  textStyle?: CSSProperties | null;
  className?: string;
}

/**
 * Size configuration
 */
interface SizeConfig {
  avatar: string;
  icon: string;
  text: string;
}

const SIZE_CONFIGS: Record<AuthorSize, SizeConfig> = {
  small: {
    avatar: "w-4 h-4",
    icon: "w-2.5 h-2.5",
    text: "text-[11px]",
  },
  medium: {
    avatar: "w-6 h-6",
    icon: "w-3.5 h-3.5",
    text: "text-xs",
  },
  large: {
    avatar: "w-8 h-8",
    icon: "w-4 h-4",
    text: "text-sm",
  },
};

/**
 * Helper to get full image URL (handles both local /static/ paths and external URLs)
 */
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith("/static/")) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

/**
 * ClickableAuthor Component
 *
 * Displays avatar + name and navigates to public profile when clicked.
 */
const ClickableAuthor = ({
  userId,
  name,
  picture,
  size = "small",
  showName = true,
  showAvatar = true,
  textStyle = null,
  className = "",
}: ClickableAuthorProps): ReactElement => {
  const navigate = useNavigate();

  const sizeConfig = SIZE_CONFIGS[size] || SIZE_CONFIGS.small;

  const handleClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    e.preventDefault();
    if (userId) {
      navigate(`/user/${userId}`);
    }
  };

  const displayName = name || "Anonymous";
  const isClickable = !!userId;

  const defaultTextStyle: CSSProperties = {
    color: "#8B6F47",
    fontFamily: "Quicksand, sans-serif",
  };
  const appliedTextStyle: CSSProperties = textStyle
    ? { ...defaultTextStyle, ...textStyle }
    : defaultTextStyle;

  return (
    <button
      onClick={isClickable ? handleClick : undefined}
      className={`flex items-center gap-1.5 transition-all ${
        isClickable ? "hover:opacity-80 cursor-pointer" : "cursor-default"
      } ${className}`}
      style={{ background: "transparent", border: "none", padding: 0 }}
      disabled={!isClickable}
    >
      {showAvatar &&
        (picture ? (
          <img
            src={getImageUrl(picture) || undefined}
            alt={displayName}
            className={`${sizeConfig.avatar} rounded-full object-cover`}
          />
        ) : (
          <div
            className={`${sizeConfig.avatar} rounded-full flex items-center justify-center`}
            style={{ background: "#FFE8D6" }}
          >
            <User className={sizeConfig.icon} style={{ color: "var(--st-primary)" }} />
          </div>
        ))}
      {showName && (
        <span
          className={`${sizeConfig.text} font-semibold truncate ${isClickable ? "hover:underline" : ""}`}
          style={appliedTextStyle}
        >
          {displayName}
        </span>
      )}
    </button>
  );
};

export default ClickableAuthor;
